// src/features/portfolio-optimization-tools/my-strategies/StrategyTearSheetPage.tsx
//
// The Strategy Tear Sheet (/dashboard/my-strategies/:reportId) — the proof
// page. It hydrates a saved report instantly, then dynamically re-computes the
// relative alpha (Active Return, Information Ratio, Hit Rate, Drawdown Defense)
// against whichever benchmark the user picks, and renders the comparative
// outperformance chart plus the absolute-metrics and holdings breakdown.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { BenchmarkSuggestionBadge } from '../../../components/BenchmarkSuggestionBadge';
import { useBenchmarkSuggestion } from '../../../hooks/useBenchmarkSuggestion';
import { dominantRegimeLabel, macroApi } from '../../../api/macro';
import { Layout, Sidebar } from '../../../shared/ui';
import {
    compareStrategyToBenchmark,
    disableRoboAdvisor,
    enableRoboAdvisor,
    executeTacticalRebalance,
    fetchBenchmarkCatalogue,
    fetchManagedDashboard,
    fetchSavedReportDetail,
    getCsvDownloadUrl,
    type AutoManagedDashboardResponse,
    type BenchmarkCompareResponse,
    type BenchmarkItem,
    type GlobalPortfolioRow,
    type SavedReportDetailResponse,
} from '../multi-asset-portfolio-optimizer/api';
import {
    MacroAllocationDonut,
    type MacroSlice,
} from '../multi-asset-portfolio-optimizer/components/MacroAllocationDonut';
import { QuantXRayTable } from '../multi-asset-portfolio-optimizer/components/QuantXRayTable';
import {
    ActivityIcon,
    ArrowLeftIcon,
    DownloadIcon,
    LoaderIcon,
    TargetIcon,
    TrendingUpIcon,
} from '../multi-asset-portfolio-optimizer/components/icons';
import { ComparativeBacktestChart } from './components/ComparativeBacktestChart';
import { ExcessReturnBarChart } from './components/ExcessReturnBarChart';
import { TacticalTradeLedger } from './components/TacticalTradeLedger';
import { WealthGenerationChart } from './components/WealthGenerationChart';

const DEFAULT_BENCHMARK_ID = '60_40_US';

interface AlphaCardProps {
    title: string;
    value: string;
    subtitle: string;
    isPositive: boolean;
    suffix?: string;
}

// A glowing card specifically for the relative alpha metrics.
const AlphaCard = ({
    title,
    value,
    subtitle,
    isPositive,
    suffix = '',
}: AlphaCardProps) => (
    <div
        className={`p-5 rounded-2xl border shadow-lg relative overflow-hidden ${
            isPositive
                ? 'bg-emerald-950/20 border-emerald-500/30'
                : 'bg-red-950/20 border-red-500/30'
        }`}
    >
        <div
            className={`absolute -right-6 -top-6 w-24 h-24 blur-3xl opacity-20 rounded-full ${
                isPositive ? 'bg-emerald-500' : 'bg-red-500'
            }`}
        />
        <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 relative z-10">
            {title}
        </h4>
        <p
            className={`text-3xl md:text-4xl font-black tracking-tight font-mono relative z-10 ${
                isPositive ? 'text-emerald-400' : 'text-red-400'
            }`}
        >
            {isPositive && parseFloat(value) > 0 ? '+' : ''}
            {value}
            {suffix}
        </p>
        <p className="text-[11px] text-slate-500 mt-2 relative z-10 font-medium">
            {subtitle}
        </p>
    </div>
);

const deriveMacroWeights = (rows: GlobalPortfolioRow[]): MacroSlice[] => {
    const bySleeve: Record<string, number> = {};
    rows.forEach((r) => {
        bySleeve[r.asset_class] =
            (bySleeve[r.asset_class] ?? 0) + r.target_weight;
    });
    const total = Object.values(bySleeve).reduce((a, b) => a + b, 0) || 1;
    return Object.entries(bySleeve)
        .map(([name, w]) => ({ name, value: (w / total) * 100 }))
        .filter((m) => m.value >= 0.05)
        .sort((a, b) => b.value - a.value);
};

export const StrategyTearSheetPage = () => {
    const { reportId } = useParams<{ reportId: string }>();
    const navigate = useNavigate();

    // Data state.
    const [report, setReport] = useState<SavedReportDetailResponse | null>(null);
    const [benchmarks, setBenchmarks] = useState<BenchmarkItem[]>([]);
    // Live macro regime (dominant ensemble probability) — the SAME value
    // the Macro Compass shows. The "Regime:" badge renders this instead of
    // the report's frozen ``macro_regime_label`` so it stays dynamic.
    const [liveRegime, setLiveRegime] = useState<string | null>(null);

    // Robo-Advisor (auto-management) state.
    const [roboDashboard, setRoboDashboard] =
        useState<AutoManagedDashboardResponse | null>(null);
    const [isRoboLoading, setIsRoboLoading] = useState(false);
    const [isRebalancing, setIsRebalancing] = useState(false);

    // Comparison state.
    const [selectedBenchId, setSelectedBenchId] =
        useState<string>(DEFAULT_BENCHMARK_ID);
    const [compareData, setCompareData] =
        useState<BenchmarkCompareResponse | null>(null);
    const [compareError, setCompareError] = useState<string | null>(null);
    // Once the user manually picks a benchmark we stop auto-applying the
    // suggestion, so their choice is never yanked out from under them.
    const [userSelectedBench, setUserSelectedBench] = useState(false);

    // Auto-suggestion driven by the saved report's risk band + realised vol.
    const {
        suggestion,
        isLoading: loadingSuggestion,
        error: suggestionError,
    } = useBenchmarkSuggestion(
        report?.risk_profile,
        report?.net_volatility,
    );

    // UI state.
    const [loadingBase, setLoadingBase] = useState(true);
    const [loadingCompare, setLoadingCompare] = useState(false);

    // 1. On mount: fetch the base report + the benchmark catalogue in parallel.
    useEffect(() => {
        if (!reportId) return;
        let cancelled = false;
        (async () => {
            try {
                const [rep, cat, regimeState] = await Promise.all([
                    fetchSavedReportDetail(reportId),
                    fetchBenchmarkCatalogue(),
                    // Live macro regime for the dynamic "Regime:" badge.
                    // Best-effort: a null (no data / 404) leaves the badge
                    // falling back to the report's stored label.
                    macroApi.getCurrentRegime().catch(() => null),
                ]);
                if (cancelled) return;
                setReport(rep);
                setBenchmarks(cat.items);
                setLiveRegime(dominantRegimeLabel(regimeState));
                // Fall back to the first catalogue entry if 60/40 is missing.
                if (
                    !cat.items.some((b) => b.id === DEFAULT_BENCHMARK_ID)
                    && cat.items.length > 0
                ) {
                    setSelectedBenchId(cat.items[0].id);
                }
                // Fetch the live Robo-Advisor dashboard only when this report
                // is auto-managed (guarded so we never 404/401 unmanaged ones).
                if (rep.is_auto_managed) {
                    try {
                        const dash = await fetchManagedDashboard(reportId);
                        if (!cancelled) setRoboDashboard(dash);
                    } catch (dashErr) {
                        // eslint-disable-next-line no-console
                        console.warn(
                            'Failed to load robo dashboard',
                            dashErr,
                        );
                    }
                }
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load tear sheet', err);
                window.alert(
                    'Failed to load strategy details. It may have been deleted.',
                );
                navigate('/dashboard/my-strategies');
            } finally {
                if (!cancelled) setLoadingBase(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [reportId, navigate]);

    // 2. On benchmark change: fetch the heavy comparison math.
    useEffect(() => {
        if (!reportId || !selectedBenchId) return;
        let cancelled = false;
        (async () => {
            setLoadingCompare(true);
            setCompareError(null);
            try {
                const comp = await compareStrategyToBenchmark(
                    reportId,
                    selectedBenchId,
                );
                if (!cancelled) setCompareData(comp);
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Benchmark comparison failed', err);
                setCompareError(
                    'Unable to compute the benchmark comparison for this window.',
                );
            } finally {
                if (!cancelled) setLoadingCompare(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [reportId, selectedBenchId]);

    // 3. Pre-select the auto-suggested benchmark once it arrives, unless the
    //    user has already made a manual choice or the id isn't in the catalogue.
    useEffect(() => {
        if (userSelectedBench || !suggestion) return;
        if (!benchmarks.some((b) => b.id === suggestion.suggested_id)) return;
        setSelectedBenchId(suggestion.suggested_id);
    }, [suggestion, benchmarks, userSelectedBench]);

    const handleSelectBenchmark = (id: string) => {
        setUserSelectedBench(true);
        setSelectedBenchId(id);
    };

    // Enrol / withdraw this strategy from Robo-Advisor auto-management.
    const handleToggleRobo = async () => {
        if (!reportId || !report) return;
        setIsRoboLoading(true);
        try {
            if (report.is_auto_managed) {
                await disableRoboAdvisor(reportId);
                setReport({ ...report, is_auto_managed: false });
                setRoboDashboard(null);
            } else {
                await enableRoboAdvisor(reportId);
                setReport({ ...report, is_auto_managed: true });
                const dash = await fetchManagedDashboard(reportId);
                setRoboDashboard(dash);
            }
        } catch (error) {
            if (error instanceof Error && error.message === 'LIMIT_REACHED') {
                window.alert(
                    'INSTITUTIONAL LIMIT REACHED (409 Conflict):\n'
                        + 'You can only have a maximum of 15 Auto-Managed '
                        + 'active strategies concurrently.',
                );
            } else {
                window.alert('Failed to toggle Forge Ultra Auto-Pilot.');
            }
        } finally {
            setIsRoboLoading(false);
        }
    };

    // Force an immediate tactical rebalance and refresh the live dashboard.
    const handleForceRebalance = async () => {
        if (!reportId) return;
        setIsRebalancing(true);
        try {
            const result = await executeTacticalRebalance(reportId);
            const dash = await fetchManagedDashboard(reportId);
            setRoboDashboard(dash);
            // Surface benign no-ops so the user gets precise feedback rather
            // than assuming trades fired when the engine had nothing to do.
            if (result.status === 'no_targets' || result.status === 'error') {
                window.alert(
                    'Forge Ultra could not generate fresh targets right now — '
                        + 'market data may still be settling for today. '
                        + 'Please try again shortly.',
                );
            } else if (result.status === 'locked_or_not_found') {
                window.alert(
                    'A rebalance is already in progress for this strategy '
                        + '(a concurrent run holds the lock). Please wait a '
                        + 'moment and refresh.',
                );
            } else if (result.reason === 'already_rebalanced_today') {
                window.alert(
                    'This strategy has already been rebalanced today. Forge Ultra '
                        + 'runs at most once per day per strategy — it will '
                        + 'evaluate fresh signals again tomorrow.',
                );
            } else if (result.status === 'skipped') {
                window.alert(
                    'This strategy is not under Forge Ultra management. '
                        + 'Enable Forge Ultra Auto-Pilot first.',
                );
            } else if (result.status === 'no_trades') {
                window.alert(
                    'Forge Ultra rebalance complete — the live book already matches '
                        + "today's optimal target, so no trades were needed.",
                );
            }
        } catch {
            window.alert('Failed to execute Forge Ultra rebalance.');
        } finally {
            setIsRebalancing(false);
        }
    };

    // Partition the catalogue: benchmarks suited to this report's risk band
    // (or universal, empty risk_profiles) first, everything else below.
    const { suitedBenchmarks, otherBenchmarks } = useMemo(() => {
        const profile = (report?.risk_profile ?? '').toLowerCase();
        const suited: BenchmarkItem[] = [];
        const other: BenchmarkItem[] = [];
        benchmarks.forEach((b) => {
            const profiles = (b.risk_profiles ?? []).map((p) =>
                p.toLowerCase(),
            );
            if (profiles.length === 0 || profiles.includes(profile)) {
                suited.push(b);
            } else {
                other.push(b);
            }
        });
        return { suitedBenchmarks: suited, otherBenchmarks: other };
    }, [benchmarks, report?.risk_profile]);

    const handleDownloadCSV = () => {
        if (!reportId || !report) return;

        // Dynamically grab the investment value from the report state so the
        // CSV's "Target Value (USD)" column matches the UI X-Ray table (same
        // logic as the `initialInvestment` memo below).
        const investment =
            report.equity_curve.length > 0
                ? report.equity_curve[0].total_invested
                : 100_000;

        window.location.href = getCsvDownloadUrl(reportId, investment);
    };

    if (loadingBase || !report) {
        return (
            <Layout sidebar={<Sidebar />} title="Strategy Analysis Highlights">
                <div className="flex flex-col items-center justify-center h-[60vh] text-slate-500">
                    <LoaderIcon className="w-10 h-10 animate-spin mb-4 text-indigo-500" />
                    <p className="font-bold tracking-wider uppercase text-xs">
                        Loading Strategy Analysis Highlights…
                    </p>
                </div>
            </Layout>
        );
    }

    const rel = compareData?.relative;
    const isBeating = rel ? rel.active_return_ann > 0 : false;
    const initialInvestment =
        report.equity_curve.length > 0
            ? report.equity_curve[0].total_invested
            : 100_000;
    const macroWeights = deriveMacroWeights(report.portfolio);
    const selectedBenchName =
        benchmarks.find((b) => b.id === selectedBenchId)?.name || 'Benchmark';

    return (
        <Layout
            sidebar={<Sidebar />}
            title="Strategy Analysis Highlights"
            subtitle="Institutional benchmark outperformance and alpha diagnostics."
        >
            {/* ========================================== */}
            {/* HEADER & CONTROLS                          */}
            {/* ========================================== */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 border-b border-slate-200 dark:border-slate-800 pb-6">
                <div>
                    <button
                        type="button"
                        onClick={() => navigate('/dashboard/my-strategies')}
                        className="text-xs font-bold text-slate-500 hover:text-indigo-500 transition-colors uppercase tracking-wider mb-2 flex items-center gap-1"
                    >
                        <ArrowLeftIcon className="w-3 h-3" /> Back to Directory
                    </button>
                    <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                        {report.name}
                    </h1>
                    <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-[10px] uppercase font-bold tracking-wider bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400 px-2.5 py-1 rounded border border-indigo-500/20">
                            {report.risk_profile} Target
                        </span>
                        <span className="text-[10px] uppercase font-bold tracking-wider bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 px-2.5 py-1 rounded border border-slate-200 dark:border-slate-700">
                            Regime: {liveRegime ?? report.macro_regime_label}
                        </span>
                        <span className="text-[10px] text-slate-500 font-semibold px-2.5 py-1">
                            Created:{' '}
                            {new Date(report.created_at).toLocaleDateString()}
                        </span>
                    </div>
                </div>

                <div className="flex items-end gap-3">
                    <div className="flex flex-col w-56">
                        <label
                            htmlFor="benchmark-select"
                            className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1"
                        >
                            Comparison vs. Benchmark
                        </label>
                        <select
                            id="benchmark-select"
                            className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg p-2 text-sm text-slate-900 dark:text-white font-semibold focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer shadow-sm"
                            value={selectedBenchId}
                            onChange={(e) =>
                                handleSelectBenchmark(e.target.value)
                            }
                            disabled={loadingCompare}
                        >
                            {suitedBenchmarks.length > 0 && (
                                <optgroup
                                    label={`Suited to ${report.risk_profile}`}
                                >
                                    {suitedBenchmarks.map((b) => (
                                        <option key={b.id} value={b.id}>
                                            {b.name} ({b.type})
                                        </option>
                                    ))}
                                </optgroup>
                            )}
                            {otherBenchmarks.length > 0 && (
                                <optgroup label="Other benchmarks">
                                    {otherBenchmarks.map((b) => (
                                        <option key={b.id} value={b.id}>
                                            {b.name} ({b.type})
                                        </option>
                                    ))}
                                </optgroup>
                            )}
                        </select>
                        <div className="mt-2">
                            <BenchmarkSuggestionBadge
                                suggestion={suggestion}
                                isLoading={loadingSuggestion}
                                error={suggestionError}
                                onApply={handleSelectBenchmark}
                                currentBenchmarkId={selectedBenchId}
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleToggleRobo}
                        disabled={isRoboLoading}
                        className={`text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg transition-colors shadow-sm flex items-center gap-2 ${
                            report.is_auto_managed
                                ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30 hover:bg-amber-500/20'
                                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                        }`}
                    >
                        {isRoboLoading
                            ? '…'
                            : report.is_auto_managed
                              ? 'Auto-Pilot Active'
                              : 'Enable Forge Ultra Auto-Pilot'}
                    </button>
                    <button
                        type="button"
                        onClick={handleDownloadCSV}
                        className="bg-slate-800 hover:bg-slate-700 text-white font-bold text-sm px-4 py-2 rounded-lg shadow-sm transition-colors flex items-center gap-2"
                    >
                        <DownloadIcon className="w-4 h-4" /> CSV
                    </button>
                </div>
            </div>

            {loadingCompare || !compareData || !rel ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3">
                    {compareError ? (
                        <p className="text-sm text-red-500 font-semibold">
                            {compareError}
                        </p>
                    ) : (
                        <LoaderIcon className="w-8 h-8 animate-spin text-indigo-500" />
                    )}
                </div>
            ) : (
                <>
                    {/* ========================================== */}
                    {/* TIER 0: ROBO-ADVISOR COMMAND CENTER        */}
                    {/* ========================================== */}
                    {roboDashboard && report.is_auto_managed && (
                        <TacticalTradeLedger
                            trades={roboDashboard.pending_trades}
                            isRebalancing={isRebalancing}
                            onExecuteRebalance={handleForceRebalance}
                            regime={roboDashboard.current_regime}
                            totalNav={roboDashboard.total_nav}
                            isLockedForToday={
                                roboDashboard.is_locked_for_today ?? false
                            }
                            lastRebalancedAt={roboDashboard.last_rebalanced_at}
                        />
                    )}

                    {/* TIER 1: Alpha KPIs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        <AlphaCard
                            title="Active Return (Alpha)"
                            value={(rel.active_return_ann * 100).toFixed(2)}
                            subtitle={`Excess CAGR vs ${selectedBenchName}`}
                            suffix="%"
                            isPositive={isBeating}
                        />
                        <AlphaCard
                            title="Information Ratio"
                            value={rel.information_ratio.toFixed(2)}
                            subtitle="Active return per unit of active risk"
                            isPositive={rel.information_ratio > 0}
                        />
                        <AlphaCard
                            title="Daily Hit Rate"
                            value={rel.hit_rate_percent.toFixed(1)}
                            subtitle="% of days the portfolio beat the benchmark"
                            suffix="%"
                            isPositive={rel.hit_rate_percent >= 50}
                        />
                        <AlphaCard
                            title="Drawdown Defense"
                            value={(rel.max_drawdown_diff * 100).toFixed(2)}
                            subtitle="Difference in peak-to-trough losses"
                            suffix="%"
                            isPositive={rel.max_drawdown_diff > 0}
                        />
                    </div>

                    {/* TIER 2: The Truth Overlay Chart */}
                    <div className="surface-card mb-8 !p-0 overflow-hidden">
                        <div className="p-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                            <h3 className="section-title text-xl flex items-center gap-2 mb-1">
                                <TargetIcon className="w-5 h-5 text-indigo-500" />
                                Relative Performance (Time-Weighted Rate of Return: TWRR)
                            </h3>
                            <p className="muted text-xs">
                                Growth of an initial{' '}
                                <span className="font-bold text-slate-300">
                                    100
                                </span>{' '}
                                (time-weighted, cash-flow neutral).{' '}
                                <span className="text-emerald-500 font-bold">
                                    Green area
                                </span>{' '}
                                = Your Strategy (net of simulated taxes &amp;
                                fees).{' '}
                                <span className="text-amber-500 font-bold">
                                    Amber dashed line
                                </span>{' '}
                                = {selectedBenchName} benchmark (gross), plotted
                                on its own right-hand scale so its trajectory
                                stays legible even when the strategy compounds
                                far above it. Both start at 100; read the
                                scale-independent cumulative alpha from the
                                tooltip.
                            </p>
                        </div>
                        <div className="p-6">
                            <ComparativeBacktestChart
                                portfolioCurve={compareData.portfolio_curve}
                                benchmarkCurve={compareData.benchmark_curve}
                                benchmarkName={selectedBenchName}
                            />
                        </div>
                    </div>

                    {/* TIER 2a: Wealth generation — TWRR alpha in real dollars */}
                    <div className="surface-card mb-8 !p-0 overflow-hidden">
                        <div className="p-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                            <h3 className="section-title text-xl flex items-center gap-2 mb-1">
                                <TrendingUpIcon className="w-5 h-5 text-emerald-500" />
                                Wealth Generation vs {selectedBenchName}
                            </h3>
                            <p className="muted text-xs">
                                The time-weighted alpha translated into
                                real-world dollars on your initial{' '}
                                <span className="font-bold text-slate-300">
                                    {new Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        maximumFractionDigits: 0,
                                    }).format(initialInvestment)}
                                </span>{' '}
                                investment. The shaded area is the cumulative
                                dollar gap your strategy generated over{' '}
                                {selectedBenchName} — compounding widens it as
                                time goes on.
                            </p>
                        </div>
                        <div className="p-6">
                            <WealthGenerationChart
                                portfolioCurve={compareData.portfolio_curve}
                                benchmarkCurve={compareData.benchmark_curve}
                                initialInvestment={initialInvestment}
                                benchmarkName={selectedBenchName}
                            />
                        </div>
                    </div>

                    {/* TIER 2b: Monthly excess-alpha attribution */}
                    <div className="surface-card mb-8 !p-0 overflow-hidden">
                        <div className="p-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                            <h3 className="section-title text-xl flex items-center gap-2 mb-1">
                                <ActivityIcon className="w-5 h-5 text-emerald-500" />
                                Monthly Excess Alpha vs {selectedBenchName}
                            </h3>
                            <p className="muted text-xs">
                                Per-month active return generated by the
                                walk-forward daemon (strategy minus benchmark,
                                time-weighted). Bars to the right of the{' '}
                                <span className="text-indigo-400 font-bold">
                                    LIVE
                                </span>{' '}
                                marker are real, forward-tracked out-of-sample
                                performance.
                            </p>
                        </div>
                        <div className="p-6">
                            <ExcessReturnBarChart
                                portfolioCurve={compareData.portfolio_curve}
                                benchmarkCurve={compareData.benchmark_curve}
                                benchmarkName={selectedBenchName}
                                backendAlpha={
                                    roboDashboard?.monthly_excess_alpha
                                }
                            />
                        </div>
                    </div>

                    {/* TIER 3: Absolute Metrics & Holdings Snapshot */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                        {/* Comparison table. */}
                        <div className="surface-card lg:col-span-1 !p-0 overflow-hidden h-fit">
                            <div className="p-5 border-b border-slate-200 dark:border-slate-800">
                                <h3 className="section-title mb-0">
                                    Absolute Metrics
                                </h3>
                            </div>
                            <table className="w-full text-left text-sm">
                                <thead className="bg-slate-50 dark:bg-slate-900/50 text-[10px] uppercase font-bold text-slate-500">
                                    <tr>
                                        <th className="px-5 py-3">Metric</th>
                                        <th className="px-5 py-3 text-right text-indigo-500">
                                            Strategy
                                        </th>
                                        <th className="px-5 py-3 text-right">
                                            Benchmark
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-800/50 font-mono">
                                    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                        <td className="px-5 py-4 font-sans text-slate-500 font-semibold">
                                            Net CAGR
                                        </td>
                                        <td className="px-5 py-4 text-right text-emerald-400 font-bold">
                                            {(
                                                compareData.portfolio_metrics
                                                    .net_expected_return_ann
                                                * 100
                                            ).toFixed(2)}
                                            %
                                        </td>
                                        <td className="px-5 py-4 text-right text-slate-400">
                                            {(
                                                compareData.benchmark_metrics
                                                    .net_expected_return_ann
                                                * 100
                                            ).toFixed(2)}
                                            %
                                        </td>
                                    </tr>
                                    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                        <td className="px-5 py-4 font-sans text-slate-500 font-semibold">
                                            Volatility
                                        </td>
                                        <td className="px-5 py-4 text-right text-slate-300">
                                            {(
                                                compareData.portfolio_metrics
                                                    .net_volatility * 100
                                            ).toFixed(2)}
                                            %
                                        </td>
                                        <td className="px-5 py-4 text-right text-slate-400">
                                            {(
                                                compareData.benchmark_metrics
                                                    .net_volatility * 100
                                            ).toFixed(2)}
                                            %
                                        </td>
                                    </tr>
                                    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                        <td className="px-5 py-4 font-sans text-slate-500 font-semibold">
                                            Max Drawdown
                                        </td>
                                        <td className="px-5 py-4 text-right text-red-400">
                                            {(
                                                compareData.portfolio_metrics
                                                    .max_drawdown * 100
                                            ).toFixed(2)}
                                            %
                                        </td>
                                        <td className="px-5 py-4 text-right text-slate-400">
                                            {(
                                                compareData.benchmark_metrics
                                                    .max_drawdown * 100
                                            ).toFixed(2)}
                                            %
                                        </td>
                                    </tr>
                                    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                        <td className="px-5 py-4 font-sans text-slate-500 font-semibold">
                                            Sharpe Ratio
                                        </td>
                                        <td className="px-5 py-4 text-right text-slate-300">
                                            {compareData.portfolio_metrics.net_sharpe.toFixed(
                                                2,
                                            )}
                                        </td>
                                        <td className="px-5 py-4 text-right text-slate-400">
                                            {compareData.benchmark_metrics.net_sharpe.toFixed(
                                                2,
                                            )}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Holdings & macro decomposition. */}
                        <div className="surface-card lg:col-span-2 flex flex-col !p-0 overflow-hidden">
                            <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
                                <h3 className="section-title mb-0 flex items-center gap-2">
                                    Snapshot Breakdown
                                </h3>
                            </div>
                            <div className="p-6 flex flex-col md:flex-row gap-6">
                                <div className="w-full md:w-1/3 min-h-[250px] flex items-center justify-center">
                                    <MacroAllocationDonut data={macroWeights} />
                                </div>
                                <div className="w-full md:w-2/3 max-h-[300px] overflow-y-auto">
                                    <QuantXRayTable
                                        rows={report.portfolio}
                                        investment={initialInvestment}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Layout>
    );
};

export default StrategyTearSheetPage;
